/* global axios */
import { DataManager } from '../helper/CacheHelper/DataManager';
import ApiClient from './ApiClient';

class CacheEnabledApiClient extends ApiClient {
  constructor(resource, options = {}) {
    super(resource, options);
    // SiteUP CRM (vazamento entre contas): o DataManager NÃO nasce aqui.
    // Os clientes são singletons de módulo (`export default new LabelsAPI()`),
    // então o construtor roda uma vez só, no carregamento do bundle. Se isso
    // acontece fora de uma URL de conta (a tela de login, por exemplo),
    // `accountIdFromRoute` é '' e o banco local vira `cw-store-`, um só para
    // todas as contas: o que a conta 11 cacheou aparecia na conta 13.
    // O banco passa a ser resolvido na hora do uso, pela conta da URL atual.
    this.dataManagersByAccount = new Map();
  }

  // eslint-disable-next-line class-methods-use-this
  get cacheModelName() {
    throw new Error('cacheModelName is not defined');
  }

  // DataManager da conta que está na URL AGORA. Sem conta na URL, não há banco
  // seguro para usar: devolve null e quem chama vai na rede.
  get dataManager() {
    const accountId = this.accountIdFromRoute;
    if (!accountId) return null;

    if (!this.dataManagersByAccount.has(accountId)) {
      this.dataManagersByAccount.set(accountId, new DataManager(accountId));
    }

    return this.dataManagersByAccount.get(accountId);
  }

  get(cache = false) {
    if (cache) {
      return this.getFromCache();
    }

    return this.getFromNetwork();
  }

  getFromNetwork() {
    return axios.get(this.url);
  }

  // eslint-disable-next-line class-methods-use-this
  extractDataFromResponse(response) {
    return response.data.payload;
  }

  // eslint-disable-next-line class-methods-use-this
  marshallData(dataToParse) {
    return { data: { payload: dataToParse } };
  }

  async getFromCache() {
    const accountId = this.accountIdFromRoute;
    const dataManager = this.dataManager;
    // Fora de uma URL de conta não se lê nem se grava cache.
    if (!dataManager) return this.getFromNetwork();

    try {
      // IDB is not supported in Firefox private mode: https://bugzilla.mozilla.org/show_bug.cgi?id=781982
      await dataManager.initDb();
    } catch {
      return this.getFromNetwork();
    }

    const { data } = await axios.get(
      `/api/v1/accounts/${accountId}/cache_keys`
    );
    const cacheKeyFromApi = data.cache_keys[this.cacheModelName];
    const isCacheValid = await this.validateCacheKey(cacheKeyFromApi);

    let localData = [];
    if (isCacheValid) {
      localData = await dataManager.get({
        modelName: this.cacheModelName,
      });
    }

    if (localData.length === 0) {
      return this.refetchAndCommit(cacheKeyFromApi);
    }

    return this.marshallData(localData);
  }

  async refetchAndCommit(newKey = null) {
    const response = await this.getFromNetwork();
    const dataManager = this.dataManager;
    if (!dataManager) return response;

    try {
      await dataManager.initDb();

      dataManager.replace({
        modelName: this.cacheModelName,
        data: this.extractDataFromResponse(response),
      });

      await dataManager.setCacheKeys({
        [this.cacheModelName]: newKey,
      });
    } catch {
      // Ignore error
    }

    return response;
  }

  async validateCacheKey(cacheKeyFromApi) {
    const dataManager = this.dataManager;
    if (!dataManager) return false;

    if (!dataManager.db) {
      await dataManager.initDb();
    }

    const cachekey = await dataManager.getCacheKey(this.cacheModelName);
    return cacheKeyFromApi === cachekey;
  }
}

export default CacheEnabledApiClient;
